import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import type { OnInit, Signal } from '@angular/core';
import {
  Component,
  DestroyRef,
  Injector,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import type { NonNullableFormBuilder } from '@angular/forms';
import { ReactiveFormsModule, Validators } from '@angular/forms';
import {
  FormHelper,
  OnyxDatepickerComponent,
  OnyxDropdownComponent,
  OnyxSectionComponent,
  OnyxTextFieldComponent,
  OnyxToggleComponent,
  OnyxUploadComponent,
  onyxDateValidator,
  onyxLengthValidator,
} from '@onyx/angular';
import { isArray, isEmpty, isEqual, omit } from 'lodash';
import { distinctUntilChanged, map, startWith } from 'rxjs';
import { Country } from '../../../../common/enums/country';
import { DictionaryCode } from '../../../../common/enums/dictionary-code';
import { DictionariesService } from '../../../../common/services/dictionaries.service';

type DriverIdentityDocumentsFormGroup = ReturnType<
  typeof DriverIdentityDocumentsFormComponent.buildForm
>;

type DriverIdentityDocumentFormGroup = ReturnType<
  (typeof DriverIdentityDocumentsFormComponent)['buildIdentityDocumentForm']
>;

@Component({
  selector: 'app-driver-identity-documents-form',
  imports: [
    OnyxSectionComponent,
    ReactiveFormsModule,
    OnyxToggleComponent,
    NgTemplateOutlet,
    OnyxTextFieldComponent,
    OnyxDropdownComponent,
    AsyncPipe,
    OnyxDatepickerComponent,
    OnyxUploadComponent,
  ],
  templateUrl: './driver-identity-documents-form.component.html',
  styleUrl: './driver-identity-documents-form.component.scss',
})
export class DriverIdentityDocumentsFormComponent implements OnInit {
  private readonly dictionariesService = inject(DictionariesService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);

  public readonly form = input.required<DriverIdentityDocumentsFormGroup>();
  public readonly nationality = input.required<Country | null>();

  protected readonly I18N = 'drivers.driverForm.identityDocuments';

  protected readonly Country = Country;

  protected readonly countries$ = this.dictionariesService.getDictionary(
    DictionaryCode.COUNTRY,
  );

  protected isInvalid!: Signal<boolean>;

  protected showPassport = signal(false);
  protected showIdentityDocument = signal(false);
  protected showResidenceCard = signal(false);
  protected showPolishCard = signal(false);

  protected isPolish = computed(() => this.nationality() === Country.PL);

  public ngOnInit(): void {
    const controls = this.form().controls;

    this.form()
      .valueChanges.pipe(
        startWith(null),
        map(() => this.form().getRawValue()),
        map((value) => {
          const hasDocument = (
            document: ReturnType<
              DriverIdentityDocumentFormGroup['getRawValue']
            >,
          ): boolean => {
            return Object.values(omit(document, 'issuingCountry')).some((v) =>
              isArray(v) ? !isEmpty(v) : !!v,
            );
          };

          return {
            showPassport: hasDocument(value.passport),
            showIdentityDocument: hasDocument(value.identityDocument),
            showResidenceCard: hasDocument(value.residenceCard),
            showPolishCard: hasDocument(value.polishCard),
          };
        }),
        distinctUntilChanged((previous, current) => isEqual(previous, current)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((value) => {
        this.showPassport.update((show) => show || value.showPassport);
        this.showIdentityDocument.update(
          (show) => show || value.showIdentityDocument,
        );
        this.showResidenceCard.update(
          (show) => show || value.showResidenceCard,
        );
        this.showPolishCard.update((show) => show || value.showPolishCard);
      });

    effect(
      () => {
        if (this.isPolish()) {
          this.showPolishCard.set(false);
        } else {
          this.showPassport.set(true);
        }
      },
      { injector: this.injector },
    );

    effect(
      () => {
        const documentControls = new Map([
          [controls.passport, { show: this.showPassport() }],
          [controls.identityDocument, { show: this.showIdentityDocument() }],
          [controls.residenceCard, { show: this.showResidenceCard() }],
          [controls.polishCard, { show: this.showPolishCard(), polish: true }],
        ]);

        for (const [control, { show, polish }] of documentControls) {
          if (!show) {
            FormHelper.reset(control);
            control.disable();
            continue;
          }

          if (control.enabled) continue;
          control.enable();

          const issuingCountryControl = control.controls.issuingCountry;
          if (polish) {
            issuingCountryControl.disable();
          } else if (!issuingCountryControl.value) {
            issuingCountryControl.setValue(this.nationality());
          }
        }
      },
      { injector: this.injector },
    );
  }

  public static buildForm(fb: NonNullableFormBuilder) {
    return fb.group({
      passport: this.buildIdentityDocumentForm(fb, {
        minLength: 8,
        maxLength: 9,
      }),
      identityDocument: this.buildIdentityDocumentForm(fb, {
        minLength: 6,
        maxLength: 15,
      }),
      residenceCard: this.buildIdentityDocumentForm(fb, {
        minLength: 6,
        maxLength: 15,
      }),
      polishCard: this.buildIdentityDocumentForm(fb, {
        minLength: 16,
        maxLength: 16,
        polish: true,
      }),
    });
  }

  private static buildIdentityDocumentForm(
    fb: NonNullableFormBuilder,
    options: {
      minLength: number;
      maxLength: number;
      polish?: boolean;
    },
  ) {
    const form = fb.group({
      number: fb.control<string | null>(null, [
        ...(options.minLength === options.maxLength
          ? [onyxLengthValidator(options.minLength)]
          : [
              Validators.minLength(options.minLength),
              Validators.maxLength(options.maxLength),
            ]),
      ]),
      issuingCountry: fb.control<Country | null>(
        options.polish ? Country.PL : null,
        [Validators.required],
      ),
      expirationDate: fb.control<string | null>(null, [
        Validators.required,
        onyxDateValidator,
      ]),
      scans: fb.control<string[]>([]),
    });
    form.disable();

    return form;
  }
}
